import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import { CharacterCount } from '@tiptap/extensions';
import type { AnyExtension, Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { HintBlock } from '@/components/extensions/hint-block';
import { IframeEmbed } from '@/components/extensions/iframe-embed';
import { MathBlock } from '@/components/extensions/math-block';
import { MathInline } from '@/components/extensions/math-inline';
import { Source } from '@/components/extensions/source';

export const tiptapStyles = (theme: Theme): SxProps<Theme> => ({
  '& .MuiTiptap-RichTextContent-root, & .MuiTiptap-RichTextContent-readOnly': {
    '& .tiptap.ProseMirror': {
      '& h1': {
        ...theme.typography.h1,
        mt: 4,
        mb: 1.4,
      },
      '& h2': {
        ...theme.typography.h2,
        mt: 4,
        mb: 1,
      },
      '& h3': {
        ...theme.typography.h3,
        mt: 3,
        mb: 1,
      },
      '& h4': {
        ...theme.typography.h4,
        mt: 2.5,
        mb: 0.75,
      },
      '& h5, & h6': {
        ...theme.typography.h5,
        mt: 2.5,
        mb: 0.75,
      },
      '& p': {
        ...theme.typography.body1,
        mb: 1,
      },
      '& a:not([data-type="mention"])': {
        color: 'text.primary',
        textDecoration: 'underline',
      },
      '& img:not(.ProseMirror-separator)': {
        my: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[100],
        padding: 2,
        maxWidth: '100%',
        height: 'auto',
        loading: 'lazy',
        // Prevent layout shift by maintaining aspect ratio
        aspectRatio: 'attr(width) / attr(height)',
      },
      '& :not(pre)>code': {
        color: (theme) => theme.palette.info.light,
      },
      '& mark': {
        padding: '1px 2px',
        borderRadius: 1,
        backgroundColor: (theme) => theme.palette.warning.light,
        color: (theme) => theme.palette.common.black,
      },
    },
  },
});

export const tiptapContentExtensions: Extensions = [
  StarterKit as unknown as AnyExtension,
  Highlight,
  Underline,
  Superscript,
  Image,
  Link,
  TaskList,
  TaskItem,
  TableKit,
  TableRow,
  TableHeader,
  TableCell,
  MathBlock,
  MathInline,
  HintBlock,
  Source,
  IframeEmbed,
];

export const tiptapEditorExtensions: Extensions = [
  StarterKit as unknown as AnyExtension,
  Highlight,
  Underline,
  Superscript,
  // Image,
  Link,
  TaskList,
  TaskItem,
  MathBlock,
  MathInline,
  HintBlock,
  CharacterCount.configure({ limit: 2000 }),
];
